/**
 * Asking a parent for permission for one child: the direct notice, the
 * affirmation, and the email (COPPA 16 CFR 312.4(c) and 312.5; 2026-10-16).
 *
 * The notice is short on purpose -- what is collected from the child, why,
 * how long, and what the parent can do -- with the full policy one tap away.
 * Saying yes here does not grant anything: it emails a single-use link to the
 * address this parent's account verified, and following that link, signed in,
 * is the consent. See family/consent.ts and services/api/valley/consent.py.
 */

import { useState } from 'react';
import { ApiError } from '../api/client';
import { Layer } from '../brand/Layer';
import { askConsent, type ConsentStatus } from '../family/consent';
import { CHILD_COLLECTION, NOT_COLLECTED, RETENTION } from '../privacy/notice';

export function ConsentSheet({
  familyId,
  personId,
  name,
  renewal = false,
  onClose,
  onSent,
}: {
  familyId: string;
  personId: string;
  name: string;
  /** The notice changed since they last said yes. */
  renewal?: boolean;
  onClose: () => void;
  onSent?: (status: ConsentStatus) => void;
}) {
  const who = name || 'your child';
  const [affirmed, setAffirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState<ConsentStatus | null>(null);

  async function send() {
    setBusy(true);
    setError('');
    try {
      const status = await askConsent(familyId, personId);
      setSent(status);
      onSent?.(status);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'That could not be sent. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Layer onClose={onClose} busy={busy}>
      <div className="sheet-layer" onClick={(event) => event.target === event.currentTarget && !busy && onClose()}>
        <div className="sheet consent-sheet" role="dialog" aria-modal="true" aria-labelledby={`consent-${personId}`}>
          <span className="sheet-handle" aria-hidden="true" />
          {sent ? (
            <>
              <h3 className="sheet-title" id={`consent-${personId}`}>Check your email</h3>
              <p className="sheet-text">
                We sent a permission link to <strong>{sent.sentTo || 'your sign-in email'}</strong>.
                Open it on a device where you are signed in to Birbly. It works once, for{' '}
                {RETENTION.consentLinkHours} hours.
              </p>
              <p className="footnote">
                Nothing about {who} is collected until you follow it. A day later we will email
                you again to confirm, with how to withdraw.
              </p>
              <div className="sheet-actions">
                <button type="button" className="primary full" onClick={onClose} data-autofocus>
                  Done
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 className="sheet-title" id={`consent-${personId}`}>
                {renewal ? `Our privacy notice changed` : `Let ${who} use Birbly`}
              </h3>
              <p className="sheet-text">
                {renewal
                  ? `Please read it again and confirm, so ${who} can keep using their page.`
                  : `Before ${who} can open their own page, design a character or move money into a goal, the law asks us to tell you what we collect from children and to get your permission.`}
              </p>

              <h4 className="consent-heading">What we collect from {who}</h4>
              <ul className="consent-list">
                {CHILD_COLLECTION.map((item) => (
                  <li key={item.what}>
                    <strong>{item.what}.</strong> {item.why}
                  </li>
                ))}
              </ul>

              <h4 className="consent-heading">What we never collect</h4>
              <p className="consent-small">{NOT_COLLECTED.join('; ')}.</p>

              <h4 className="consent-heading">Who sees it</h4>
              <p className="consent-small">
                Only your family. No advertising, no analytics, no selling or sharing. Amazon Web
                Services stores it for us as our service provider.
              </p>

              <h4 className="consent-heading">How long</h4>
              <p className="consent-small">
                Character drafts nobody approves: {RETENTION.draftDays} days. An earlier character
                kept for undo: {RETENTION.previousDays} days. A device of theirs unused for{' '}
                {RETENTION.deviceIdleDays} days is unlinked. The rest until you remove {who} or
                delete your family; then it leaves our recovery copies within{' '}
                {RETENTION.recoveryDays} days.
              </p>

              <h4 className="consent-heading">Your choices</h4>
              <p className="consent-small">
                In Settings, Privacy you can see what we hold about {who}, pause their access,
                withdraw this permission, or delete their information, at any time.
              </p>

              <p className="consent-small">
                <a href="/privacy" target="_blank" rel="noreferrer">Read the full privacy notice</a>
              </p>

              <label className="check-field consent-affirm">
                <input type="checkbox" checked={affirmed} onChange={(event) => setAffirmed(event.target.checked)} />
                <span>
                  I am {who}&rsquo;s parent or legal guardian, and I give permission for Birbly to
                  collect and use this information as described.
                </span>
              </label>

              {error ? <p role="alert" className="error">{error}</p> : null}

              <div className="sheet-actions">
                <button aria-busy={busy} type="button" className="primary full" disabled={!affirmed || busy} onClick={() => void send()}>
                  {busy ? 'Sending…' : 'Email me the permission link'}
                </button>
                <button type="button" className="text-button sheet-cancel" disabled={busy} onClick={onClose}>
                  Not now
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layer>
  );
}
